import { Segmented, Tooltip } from 'antd';
import type { AgentDefinition } from '@/api/types';
import { StatusPill, type PillTone } from './StatusPill';

// AN AGENT'S LIFECYCLE STATUS (ai.AiAgent.StatusId; the server's one home is OO.Domain.AIInfo.AgentLifecycleStatus),
// 2 Nov 2026. Founder: "we do all the evals, when we are sure that agent is ready for production, we make that active."
// Active means production-ready; an agent never given a status has not been approved and counts as Draft. Every list
// and picker of agents offers the same toggle (All · Active · Draft · Inactive · Archived) and every row says which.

export type AgentStatusKey = 'active' | 'draft' | 'inactive' | 'archived';
export type AgentStatusFilter = 'all' | AgentStatusKey;

/** The ids in ai.AiAgentStatus (verified 2 Nov 2026). */
export const AGENT_STATUS_ID: Record<AgentStatusKey, number> = { draft: 1, active: 2, inactive: 3, archived: 4 };

const LOOK: Record<AgentStatusKey, { label: string; tone: PillTone; meaning: string }> = {
  active: { label: 'Active', tone: 'green', meaning: 'Approved for production' },
  draft: { label: 'Draft', tone: 'amber', meaning: 'Being built or evaluated; not approved for production yet' },
  inactive: { label: 'Inactive', tone: 'slate', meaning: 'Was active and has been switched off' },
  archived: { label: 'Archived', tone: 'slate', meaning: 'Retired and kept for the record' },
};

/** The agent's status; never given one (null) is Draft. */
export function agentStatusOf(agent: Pick<AgentDefinition, 'statusId'> | null | undefined): AgentStatusKey {
  switch (agent?.statusId) {
    case AGENT_STATUS_ID.active:
      return 'active';
    case AGENT_STATUS_ID.inactive:
      return 'inactive';
    case AGENT_STATUS_ID.archived:
      return 'archived';
    default:
      return 'draft';
  }
}

export function agentStatusLabel(key: AgentStatusKey): string {
  return LOOK[key].label;
}

export function agentStatusMeaning(key: AgentStatusKey): string {
  return LOOK[key].meaning;
}

/** The status as a pill, with what it means on hover. */
export function AgentStatusPill({ agent }: { agent: Pick<AgentDefinition, 'statusId'> }) {
  const key = agentStatusOf(agent);
  const hint = agent.statusId == null ? 'Never given a status, so it counts as Draft until someone makes it Active' : LOOK[key].meaning;
  return (
    <Tooltip title={hint}>
      <span data-testid="agent-status-pill">
        <StatusPill tone={LOOK[key].tone}>{LOOK[key].label}</StatusPill>
      </span>
    </Tooltip>
  );
}

export const AGENT_STATUS_FILTERS: { value: AgentStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
];

/** The one status toggle every agent list and picker shows. */
export function AgentStatusToggle({
  value,
  onChange,
  size = 'small',
}: {
  value: AgentStatusFilter;
  onChange: (next: AgentStatusFilter) => void;
  size?: 'small' | 'middle';
}) {
  return (
    <Segmented<AgentStatusFilter>
      aria-label="Agent status"
      size={size}
      value={value}
      onChange={onChange}
      options={AGENT_STATUS_FILTERS}
    />
  );
}

/** The directory's status word for a toggle value (undefined: all). */
export function statusParam(filter: AgentStatusFilter): AgentStatusKey | undefined {
  return filter === 'all' ? undefined : filter;
}
